import React, { useCallback, useContext, useMemo, useState } from "react";
import { Dropdown, FormControl } from "react-bootstrap";
import { Check, Pencil, Star, StarFill } from "react-bootstrap-icons";
import { TradingContext } from "../../contexts/TradingContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import analyticsService from "../../services/analyticsService";
import { formatBrokerDisplayName } from "../../utils/brokerConnection";
import { accountNumber } from "../../utils/canonicalFields";
import {
  getAccountDisplayLabel,
  getAccountNickname,
  withAccountNickname,
} from "../../utils/accountNickname";
import { useUpdateAccountDefaults, useUpdateAppSettings } from "../../hooks/useBackend";
import { FAVORITE_STAR_COLOR } from "../../utils/favoriteTickers";
import { isStarredAccount } from "../../utils/starredAccount";

function accountBrokerKey(account) {
  return String(account?.broker_name || account?.institution_name || account?.ct_connection_id || 'Broker').toUpperCase();
}

function resolveSelectedBrokerLabel(account, user) {
  if (!account) return null;
  if (account.broker_name || account.institution_name) {
    return formatBrokerDisplayName(account.broker_name || account.institution_name);
  }
  if (account.adapter_type === 'tradestation_direct') {
    return formatBrokerDisplayName('TRADESTATION');
  }
  const connId = account.ct_connection_id || account.connection_id;
  const match = (user?.brokerConnections || []).find((c) => {
    if (connId && c.connection_id && String(c.connection_id) === String(connId)) return true;
    return false;
  });
  if (match?.broker_name) return formatBrokerDisplayName(match.broker_name);
  return null;
}

function accountKey(account) {
  return `${account?.AccountID}-${account?.isPaperTrading ? 'p' : 'l'}`;
}

/** Whole control is the toggle so the menu anchors under the full dropdown, not just the caret. */
const AccountDropdownToggle = React.forwardRef(function AccountDropdownToggle(
  {
    onClick,
    compact,
    hideLabel,
    desktopDense,
    fontSize,
    colors,
    selectionTitle,
    selectedBrokerLabel,
    selectedAccountResolved,
    shouldBlurAccountName,
    displayLabel,
    getTradingTypePillStyle,
    ...props
  },
  ref
) {
  const padY = compact ? 6 : hideLabel ? 10 : 6;
  const padX = compact ? 4 : hideLabel ? 4 : desktopDense ? 8 : 8;

  return (
    <div
      {...props}
      ref={ref}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(e);
      }}
      title={selectionTitle || undefined}
      aria-label={selectionTitle ? `Account ${selectionTitle}` : undefined}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: 0,
        width: hideLabel ? '100%' : 'fit-content',
        maxWidth: '100%',
        cursor: 'pointer',
        backgroundColor: compact ? colors.bgHeader : (hideLabel ? colors.bgHeader : colors.bgInput),
        borderRadius: compact || hideLabel ? '8px' : '4px',
        height: hideLabel ? '40px' : (compact ? '40px' : (desktopDense ? '32px' : 'auto')),
        padding: `${padY}px ${padX}px`,
        gap: 4,
      }}
    >
      {selectedBrokerLabel && (
        <span
          style={{
            flexShrink: 0,
            fontSize: compact ? '10px' : desktopDense ? '10px' : '11px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: colors.textMuted,
            maxWidth: compact ? 72 : 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedBrokerLabel}
        </span>
      )}
      <span
        style={{
          flex: hideLabel ? '1 1 auto' : '0 1 auto',
          minWidth: 0,
          maxWidth: compact ? '100%' : (hideLabel ? '100%' : selectedBrokerLabel ? '160px' : '140px'),
          fontSize: compact ? '13px' : fontSize,
          color: compact ? colors.text : colors.primary,
          fontWeight: '500',
          textAlign: compact ? 'center' : 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: compact ? '1.2' : 'normal',
          filter: shouldBlurAccountName ? 'blur(4px)' : 'none',
          transition: 'filter 0.2s ease',
        }}
      >
        {displayLabel}
      </span>
      {selectedAccountResolved && (
        <span style={getTradingTypePillStyle(selectedAccountResolved.isPaperTrading)}>
          {selectedAccountResolved.isPaperTrading ? 'SIM' : 'LIVE'}
        </span>
      )}
      {!compact && (
        <span
          style={{
            color: colors.primary,
            fontSize,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            lineHeight: 1,
            marginLeft: 0,
          }}
        >
          <i className="fas fa-caret-down" />
        </span>
      )}
    </div>
  );
});

const AccountDropdown = ({ compact = false, hideLabel = false, dense = false, labelTailChars = null, sizeToContent = false }) => {
  const { colors } = useTheme();
  const { user, updateUserSettings } = useContext(AuthContext);
  const { selectedAccount, setSelectedAccount, accounts, isLoading } = useContext(TradingContext);
  const { updateAccountDefaults } = useUpdateAccountDefaults();
  const { updateAppSettings } = useUpdateAppSettings();
  const [showDropdown, setShowDropdown] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draftNickname, setDraftNickname] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [savingStar, setSavingStar] = useState(false);

  const shouldBlurAccountName = user?.appSettings?.blurAccountName === true;
  const accountDefaults = user?.accountSettings || user?.accountDefaults || {};

  const brokerConnectionCount = user?.brokerConnections?.length || 0;
  const groupedAccounts = useMemo(() => {
    if (!accounts?.length) return [];
    const sorted = [...accounts].sort((a, b) => {
      if (a.isPaperTrading === b.isPaperTrading) return 0;
      return a.isPaperTrading ? 1 : -1;
    });
    const brokerKeys = new Set(sorted.map(accountBrokerKey));
    const shouldGroup = brokerConnectionCount > 1 || brokerKeys.size > 1;
    if (!shouldGroup) {
      return [{ broker: null, accounts: sorted }];
    }
    const byBroker = new Map();
    for (const acc of sorted) {
      const key = accountBrokerKey(acc);
      if (!byBroker.has(key)) byBroker.set(key, []);
      byBroker.get(key).push(acc);
    }
    return [...byBroker.entries()].map(([broker, list]) => ({ broker, accounts: list }));
  }, [accounts, brokerConnectionCount]);

  const showBrokerInSelection = useMemo(() => {
    if (brokerConnectionCount > 1) return true;
    if (!accounts?.length) return false;
    return new Set(accounts.map(accountBrokerKey)).size > 1;
  }, [accounts, brokerConnectionCount]);

  const selectedAccountResolved = useMemo(() => {
    if (!selectedAccount?.AccountID || !accounts?.length) return selectedAccount;
    const full = accounts.find(
      (a) =>
        a.AccountID === selectedAccount.AccountID &&
        (a.isPaperTrading == null || a.isPaperTrading === selectedAccount.isPaperTrading)
    );
    return full || selectedAccount;
  }, [selectedAccount, accounts]);

  const selectedAccountHasNickname = useMemo(() => {
    if (!selectedAccountResolved?.AccountID) return false;
    const id = accountNumber(selectedAccountResolved) || selectedAccountResolved.AccountID;
    return !!getAccountNickname(accountDefaults, id);
  }, [selectedAccountResolved, accountDefaults]);

  const selectedBrokerLabel = showBrokerInSelection && !selectedAccountHasNickname
    ? resolveSelectedBrokerLabel(selectedAccountResolved, user)
    : null;

  const saveNickname = useCallback(async (account, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user?.token || !account?.AccountID || savingNickname) return;
    const id = accountNumber(account) || account.AccountID;
    setSavingNickname(true);
    try {
      const next = withAccountNickname(accountDefaults, id, draftNickname);
      // AuthContext mirrors accountDefaults from accountSettings — pass both.
      updateUserSettings({ accountSettings: next, accountDefaults: next });
      await updateAccountDefaults(user.token, next);
      setEditingKey(null);
      setDraftNickname('');
    } catch (err) {
      console.error('[AccountDropdown] Failed to save nickname:', err);
    } finally {
      setSavingNickname(false);
    }
  }, [user?.token, accountDefaults, draftNickname, savingNickname, updateAccountDefaults, updateUserSettings]);

  const toggleDefaultAccount = useCallback(async (account, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user?.token || !account?.AccountID || savingStar) return;
    const currentlyStarred = isStarredAccount(user.appSettings, account);
    const patch = currentlyStarred
      ? { starredAccountId: undefined, starredAccountPaper: undefined }
      : { starredAccountId: account.AccountID, starredAccountPaper: !!account.isPaperTrading };
    setSavingStar(true);
    try {
      const result = await updateAppSettings(user.token, patch);
      if (result?.error || result?.Message) {
        console.error('[AccountDropdown] Failed to save default account:', result);
      }
    } catch (err) {
      console.error('[AccountDropdown] Failed to save default account:', err);
    } finally {
      setSavingStar(false);
    }
  }, [user?.token, user?.appSettings, savingStar, updateAppSettings]);

  if (!isLoading && (!accounts || accounts.length === 0)) {
    return null;
  }

  const handleAccountSelect = (account, e) => {
    if (e) e.stopPropagation();
    if (editingKey) return;

    if (
      selectedAccount &&
      account.AccountID === selectedAccount.AccountID &&
      account.isPaperTrading === selectedAccount.isPaperTrading
    ) {
      setShowDropdown(false);
      return;
    }

    analyticsService.trackTradingEvent('account_switched', {
      from_account: selectedAccount?.AccountID,
      to_account: account.AccountID,
      to_account_type: account.AccountType,
      is_paper_trading: account.isPaperTrading
    });

    setSelectedAccount(account);
    setShowDropdown(false);
  };

  const maskAccountId = (id) => {
    const s = String(id ?? '');
    const tail = Number.isFinite(labelTailChars) && labelTailChars > 0 ? labelTailChars : null;
    if (!tail || s.length <= tail) return s;
    return s.slice(-tail);
  };

  const formatClosedLabel = (account) => {
    if (!account) return isLoading ? 'Loading…' : 'Select account';
    const label = getAccountDisplayLabel(account, accountDefaults);
    const nick = getAccountNickname(accountDefaults, accountNumber(account) || account.AccountID);
    // Nicknames are already short/human — don't mask. Mask raw IDs on narrow mobile headers.
    if (nick) return label;
    return maskAccountId(label);
  };

  const fullAccountIdForTitle = selectedAccountResolved
    ? String(selectedAccountResolved.AccountID ?? '')
    : '';
  const closedDisplayLabel = formatClosedLabel(selectedAccountResolved);
  const selectionTitle = [
    selectedBrokerLabel,
    getAccountNickname(accountDefaults, fullAccountIdForTitle) || null,
    fullAccountIdForTitle || null,
  ]
    .filter(Boolean)
    .join(' · ');

  const startEdit = (account, e) => {
    e.preventDefault();
    e.stopPropagation();
    const id = accountNumber(account) || account.AccountID;
    const existing = getAccountNickname(accountDefaults, id);
    setEditingKey(accountKey(account));
    setDraftNickname(existing || String(id ?? ''));
  };

  const cancelEdit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEditingKey(null);
    setDraftNickname('');
  };

  const getTradingTypePillStyle = (isPaperTrading) => {
    const pillDense = dense && !compact;
    return {
      display: 'inline-block',
      padding: compact ? '2px 4px' : pillDense ? '2px 5px' : '2px 5px',
      borderRadius: '10px',
      fontSize: compact ? '9px' : pillDense ? '9px' : '10px',
      fontWeight: '600',
      textTransform: 'uppercase',
      backgroundColor: isPaperTrading ? colors.warning : colors.success,
      color: 'white',
      marginLeft: 0,
      marginRight: 0,
      lineHeight: compact || pillDense ? '1' : 'normal',
      flexShrink: 0,
    };
  };

  const desktopDense = dense && !compact && !hideLabel;
  const fontSize = compact ? '0.75rem' : desktopDense ? '0.8125rem' : '0.875rem';

  const iconBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: colors.textMuted,
    padding: 4,
    borderRadius: 4,
    cursor: 'pointer',
    flexShrink: 0,
    lineHeight: 1,
  };

  const renderAccountItem = (account) => {
    const key = accountKey(account);
    const isSelected =
      account.AccountID === selectedAccount?.AccountID &&
      account.isPaperTrading === selectedAccount?.isPaperTrading;
    const isEditing = editingKey === key;
    const label = getAccountDisplayLabel(account, accountDefaults);
    const isDefault = isStarredAccount(user?.appSettings, account);

    return (
      <Dropdown.Item
        key={key}
        as="div"
        // Prevent select while editing; otherwise normal click selects.
        onClick={(e) => {
          if (isEditing) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          handleAccountSelect(account, e);
        }}
        style={{
          color: isSelected ? colors.primary : colors.text,
          backgroundColor: isSelected ? `${colors.primary}1A` : 'transparent',
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: isSelected ? '600' : '400',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          cursor: isEditing ? 'default' : 'pointer',
        }}
        onMouseEnter={(e) => {
          if (!isSelected && !isEditing) {
            e.currentTarget.style.backgroundColor = `${colors.primary}0D`;
            e.currentTarget.style.color = colors.primary;
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected && !isEditing) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = colors.text;
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 6 }}>
          {isEditing ? (
            <>
              <FormControl
                size="sm"
                autoFocus
                value={draftNickname}
                disabled={savingNickname}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setDraftNickname(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') saveNickname(account, e);
                  if (e.key === 'Escape') cancelEdit(e);
                }}
                placeholder={String(account.AccountID ?? '')}
                style={{
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: colors.bgSecondary || colors.bgCard,
                  borderColor: colors.border,
                  color: colors.text,
                  fontSize: '0.875rem',
                }}
              />
              <button
                type="button"
                title="Save nickname"
                aria-label="Save nickname"
                disabled={savingNickname}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => saveNickname(account, e)}
                style={{ ...iconBtnStyle, color: colors.primary }}
              >
                <Check size={16} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                title={isDefault ? 'Default when the app loads' : 'Use as default when the app loads'}
                aria-label={
                  isDefault
                    ? `Remove default account ${label}`
                    : `Set ${label} as the default account`
                }
                aria-pressed={isDefault}
                disabled={savingStar}
                data-testid={`default-account-star-${account.AccountID}`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => toggleDefaultAccount(account, e)}
                style={{
                  ...iconBtnStyle,
                  color: isDefault ? FAVORITE_STAR_COLOR : colors.textMuted,
                }}
              >
                {isDefault ? <StarFill size={14} /> : <Star size={14} />}
              </button>
              <span
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  filter: shouldBlurAccountName ? 'blur(4px)' : 'none',
                  transition: 'filter 0.2s ease',
                }}
                title={String(account.AccountID ?? '')}
              >
                {label}
              </span>
              <button
                type="button"
                title="Edit account nickname"
                aria-label="Edit account nickname"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => startEdit(account, e)}
                style={iconBtnStyle}
              >
                <Pencil size={12} />
              </button>
              <span style={getTradingTypePillStyle(account.isPaperTrading)}>
                {account.isPaperTrading ? 'SIM' : 'LIVE'}
              </span>
            </>
          )}
        </div>
      </Dropdown.Item>
    );
  };

  return (
    <div 
      onClick={(e) => e.stopPropagation()}
      style={{ 
      display: 'inline-flex', 
      flexDirection: 'row',
      gap: compact ? '0' : (hideLabel ? '0' : '4px'), 
      alignItems: 'center', 
      minWidth: 0,
      width: hideLabel ? '100%' : (sizeToContent || compact ? 'fit-content' : 'auto'),
      maxWidth: compact && !hideLabel ? '100%' : undefined,
      backgroundColor: compact ? colors.bgHeader : (hideLabel ? colors.bgHeader : 'transparent'),
      borderRadius: compact ? '8px' : (hideLabel ? '8px' : '0'),
      position: 'relative',
      overflow: 'visible',
      zIndex: showDropdown ? 1200 : 'auto',
    }}>
      <Dropdown 
        show={showDropdown} 
        onToggle={(isOpen) => {
          setShowDropdown(isOpen);
          if (!isOpen) {
            setEditingKey(null);
            setDraftNickname('');
          }
        }}
        style={{ 
          display: 'block',
          minWidth: 0,
          width: hideLabel ? '100%' : 'fit-content',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
        drop="down"
        align={compact ? 'end' : 'start'}
      >
        <Dropdown.Toggle
          as={AccountDropdownToggle}
          compact={compact}
          hideLabel={hideLabel}
          desktopDense={desktopDense}
          fontSize={fontSize}
          colors={colors}
          selectionTitle={selectionTitle}
          selectedBrokerLabel={selectedBrokerLabel}
          selectedAccountResolved={selectedAccountResolved}
          shouldBlurAccountName={shouldBlurAccountName}
          displayLabel={closedDisplayLabel}
          getTradingTypePillStyle={getTradingTypePillStyle}
        />
        
        <Dropdown.Menu 
          align={compact ? { sm: 'end' } : { sm: 'start' }}
          style={{
            position: 'absolute',
            top: '100%',
            left: compact ? 'auto' : 0,
            right: compact ? 0 : 'auto',
            bottom: 'auto',
            transform: 'none',
            marginTop: 4,
            backgroundColor: colors.bgInput,
            border: `1px solid ${colors.borderLight}`,
            borderRadius: '8px',
            padding: '8px 0',
            minWidth: Math.max(compact ? 200 : 220, 0),
            maxWidth: 'min(90vw, 320px)',
            width: 'max-content',
            zIndex: 1200,
          }}
        >
          {groupedAccounts.map((group) => (
            <React.Fragment key={group.broker || 'all'}>
              {group.broker && (
                <div
                  style={{
                    padding: '6px 12px 4px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                  }}
                >
                  {formatBrokerDisplayName(group.broker)}
                </div>
              )}
              {group.accounts.map(renderAccountItem)}
            </React.Fragment>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default AccountDropdown;
